import React, { useState, useMemo } from 'react';
import { Dialog, Input, Button, Tooltip, MessagePlugin } from 'tdesign-react';
import { Form } from '../../../ui/universe';
import OcrTag from '../../../ui/Tag/index.jsx';

/**
 * NewItemNames 字段输入组件
 * 支持文本字段（KeyType=0）和表格字段（KeyType=1）两组，
 * 每组下方有「+ 文本key / + 表格key」按钮，点击按钮弹出 Dialog 输入字段名。
 * 已添加的字段以 OcrTag 展示：
 *   - 点击 tag：气泡（Popconfirm）内配置 KeyPrompt
 *   - 点击 × ：删除该字段
 */
function NewItemNamesField({ params, value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const keyNameMax = params?.KeyNameMax || 64;
  const keyPromptMax = params?.KeyPromptMax || 128;

  // 新增弹窗
  const [addDialog, setAddDialog] = useState({
    visible: false,
    keyType: 0,
    keyName: '',
  });

  // Prompt 气泡（Popconfirm）状态：使用 items 数组下标作为 currentKey
  const [currentKey, setCurrentKey] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const textFields = useMemo(
    () => items.map((i, idx) => ({ item: i, index: idx })).filter((x) => x.item.KeyType === 0),
    [items]
  );
  const tableFields = useMemo(
    () => items.map((i, idx) => ({ item: i, index: idx })).filter((x) => x.item.KeyType === 1),
    [items]
  );

  const openAddDialog = (keyType) => {
    setAddDialog({ visible: true, keyType, keyName: '' });
  };

  const closeAddDialog = () => {
    setAddDialog((s) => ({ ...s, visible: false }));
  };

  const handleAddConfirm = () => {
    const { keyType, keyName } = addDialog;
    const name = (keyName || '').trim();
    if (!name) {
      MessagePlugin.warning('请输入字段名称');
      return;
    }
    if (items.some((i) => i.KeyName === name && i.KeyType === keyType)) {
      MessagePlugin.warning('字段名称已存在');
      return;
    }
    onChange([...items, { KeyName: name, KeyType: keyType }]);
    closeAddDialog();
  };

  const handleRemoveByIndex = (index) => {
    const removed = items[index];
    const next = items.slice();
    next.splice(index, 1);
    onChange(next);
    // 如果删除的正是当前打开气泡的那个，收起气泡
    if (removed && currentKey === `${removed.KeyType}-${index}`) {
      setCurrentKey(null);
      setShowConfirm(false);
    }
  };

  const handlePromptConfirm = (index, val) => {
    const next = items.slice();
    const prompt = (val?.KeyPrompt || '').trim();
    next[index] = { ...next[index] };
    if (prompt) next[index].KeyPrompt = prompt;
    else delete next[index].KeyPrompt;
    onChange(next);
  };

  const renderFieldGroup = (keyType, label, desc, fields) => {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>{label}</span>
          <Tooltip content={desc} theme="light">
            <span
              className="ocrDemo-icon-question"
              style={{ marginLeft: 4, cursor: 'help', color: '#999' }}
            ></span>
          </Tooltip>
        </div>
        <div
          className="new-item-names-field__tags"
          style={{
            border: '1px solid #dcdfe6',
            borderRadius: 6,
            padding: '8px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'center',
            background: '#fff',
            minHeight: 36,
          }}
        >
          {fields.length === 0 ? (
            <span style={{ color: '#c0c4cc', fontSize: 13 }}>
              {keyType === 0
                ? '点击下方「+ 文本key」添加文本字段'
                : '点击「+ 表格key」添加表格列字段'}
            </span>
          ) : (
            fields.map(({ item, index }) => {
              const tagKey = `${keyType}-${index}`;
              return (
                <OcrTag
                  key={tagKey}
                  keyVal={tagKey}
                  currentKey={currentKey}
                  theme="primary"
                  content={item.KeyName}
                  keyPrompt={item.KeyPrompt || ''}
                  maxPrompt={keyPromptMax}
                  titleText={`配置提示词 · ${item.KeyName}`}
                  onChoose={(k) => setCurrentKey(k)}
                  setShowConfirm={(val) => {
                    setShowConfirm(val);
                    if (!val) setCurrentKey(null);
                  }}
                  onConfirm={(val) => handlePromptConfirm(index, val)}
                  onClose={() => handleRemoveByIndex(index)}
                />
              );
            })
          )}
        </div>
      </div>
    );
  };

  const isTextType = addDialog.keyType === 0;

  return (
    <div>
      {renderFieldGroup(
        0,
        '文本字段',
        '点击已添加字段，可自定义该字段的 Prompt 指令。',
        textFields
      )}
      {renderFieldGroup(
        1,
        '表格字段',
        '点击已添加字段，可自定义该字段的 Prompt 指令。',
        tableFields
      )}
      <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
        <Button
          theme="default"
          variant="outline"
          size="small"
          onClick={() => openAddDialog(0)}
        >
          + 文本key
        </Button>
        <Button
          theme="default"
          variant="outline"
          size="small"
          onClick={() => openAddDialog(1)}
        >
          + 表格key
        </Button>
      </div>

      {/* 新增字段弹窗（文本 / 表格共用） */}
      <Dialog
        visible={addDialog.visible}
        onClose={closeAddDialog}
        header={isTextType ? '添加自定义文本字段' : '添加自定义表格字段'}
        width={420}
        onCancel={closeAddDialog}
        onConfirm={handleAddConfirm}
        confirmBtn="确定"
        cancelBtn="取消"
        destroyOnClose
      >
        <Form>
          <Form.Item label="字段名称">
            <Input
              value={addDialog.keyName}
              onChange={(v) => setAddDialog((s) => ({ ...s, keyName: v }))}
              placeholder={
                isTextType
                  ? '请输入字段名称，如：发票号码'
                  : '请输入表格列字段名称，如：商品名称'
              }
              maxlength={keyNameMax}
              autofocus
            />
          </Form.Item>
        </Form>
      </Dialog>
    </div>
  );
}

export default NewItemNamesField;
